import {
  AuthHeader,
  BusinessProfileForm,
  OnboardingSuccessful,
  OTPForm,
  PersonalProfileForm,
  SignUpForm,
} from './components';
import { Link, useOutletContext, useLocation } from 'react-router';
import { routes } from '@/lib/constants';
import { useEffect, useState } from 'react';
import type { OutletContext } from '@/components/layouts';
import { useUser } from '@/api';
import { getAuthToken } from '@/lib/auth/auth-token';

export const SignUpPage = () => {
  const location = useLocation();
  const locationState = (location.state ?? {}) as {
    userId?: number;
    identifier?: string;
  };
  const [userId, setUserId] = useState<number | undefined>(
    locationState.userId
  );
  const [identifier, setIdentifier] = useState<string>(
    locationState.identifier ?? ''
  );

  const { step, decrementStep } = useOutletContext<OutletContext>();

  // Keep local identifier in sync when navigation state carries the phone
  // (e.g. after personal profile → OTP, or Google Response B → OTP).
  useEffect(() => {
    const fromState = (
      (location.state ?? {}) as { identifier?: string }
    ).identifier?.trim();
    if (fromState) {
      setIdentifier(fromState);
    }
  }, [location.state]);

  // login/google Response B lands on OTP without a phone in the payload —
  // hydrate WhatsApp from /users/me once the session token exists.
  const hasSession = Boolean(getAuthToken());
  const { data: me } = useUser(
    step === 2 && !identifier && hasSession ? 'me' : undefined
  );

  useEffect(() => {
    if (me?.whatsappNumber && !identifier) {
      setIdentifier(me.whatsappNumber);
    }
    if (me?.id != null && userId == null) {
      setUserId(me.id);
    }
  }, [me, identifier, userId]);

  return (
    <div className="flex flex-col gap-10 overflow-y-auto w-full h-full">
      {step === 0 && (
        <AuthHeader
          heading="Create a free account"
          description="Unlock your potential and turn your passion into profit! Let's start this journey together!"
        />
      )}
      {step === 1 && (
        <AuthHeader
          heading="Create a Sellyt Account"
          description="Start selling smarter in minutes, make money while you sleep."
        />
      )}
      {step === 2 && (
        <AuthHeader
          heading="Verify your phone number"
          description={
            identifier
              ? `Enter the verification code sent to ${identifier} on WhatsApp and your email.`
              : 'Enter the verification code sent to your WhatsApp and email.'
          }
        />
      )}
      {step === 3 && (
        <AuthHeader
          heading="Build Your Business Profile"
          description="Set up the basics so Sellyt can help you sell smarter, faster."
        />
      )}
      {step === 0 && <SignUpForm />}
      {step === 1 && (
        <PersonalProfileForm
          setIdentifier={setIdentifier}
          setUserId={setUserId}
        />
      )}
      {step === 2 && (
        <OTPForm
          identifier={identifier}
          setUserId={setUserId}
          type="phone-verification"
        />
      )}
      {step === 3 && <BusinessProfileForm userId={userId} />}
      {step === 4 && <OnboardingSuccessful />}
      {step !== 4 && (
        <div
          className="text-xs md:text-sm px-4 md:px-6 lg:px-8 -mt-6"
          onClick={() => decrementStep()}
        >
          Already have an account?{' '}
          <Link to={routes.login} className="text-primary">
            Sign in
          </Link>
        </div>
      )}
    </div>
  );
};
